import { React, useState, useEffect } from 'react'
import { motion } from 'framer-motion'

import './Homepage.scss'

import { useDocumentTitle, useDocumentConfig } from 'hooks/useDocument'
import { useFetch } from 'hooks/useFetch'

import Loader from 'components/Loader'
import Hero from 'modules/Hero'
import Button from 'components/Button'
import Card from 'components/Card'

const Homepage = () => {
	useDocumentTitle()
	useDocumentConfig(`blue`, false)

	const [projects, setProjects] = useState([])
	const { loading, data } = useFetch(`/realisations/listing.json`, 'json')

	useEffect(() => {
		if (data === null) {
			return
		}

		const filtered = data.filter(project => project.highlight === true)
		const randomized = filtered.map(value => ({ value, sort: Math.random() })).sort((a, b) => a.sort - b.sort).map(({ value }) => value)
		setProjects(randomized.slice(0,3))
	}, [data])

	return (
		<motion.main
			className='homepage'
			initial={{ opacity: 0, transition: { duration: 0.3 } }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0, transition: { duration: 0.3 } }}
		>
			<Hero
				scene={1}
				scrollTo="Découvrir l'agence"
				smallerText={true}
			>
				L'agence web design & développement
				<br />
				pour des projets sur-mesure,
				<br />
				durables et efficaces.
			</Hero>

			<section className='homepage-agency'>
				<div className='column-full align-center'>
					<h1>BIG et Nouf, un duo créatif & technique</h1>
					<p className='light'>
						Direction artistique et développement front unifiés, pour une exécution fluide, exigeante et humaine.<br />
						Design sur-mesure, code fiable, accompagnement pro.
					</p>
					<Button to={`${process.env.PUBLIC_URL}/contact`}>Nous contacter</Button>
				</div>
			</section>

			<section className='homepage-offers'>
				<h2 className='align-center'>Nos offres adaptées à vos besoins</h2>
				<div className='columns homepage-offers-list'>
					<div className='column-third'>
						<div className='offer-card'>
							<h3>Site clé en main</h3>
							<ul className='offer-features no-prefix'>
								<li>Site sur-mesure, compatible tous écrans</li>
								<li>5 pages, extensible selon vos besoins</li>
								<li>Référencement Google optimisé</li>
								<li>Hébergement + nom de domaine inclus</li>
								<li>Formation simple à l'édition</li>
								<li>Livraison en 4 à 6 semaines</li>
							</ul>
							<div className='offer-price'>
								<strong>3500 à 5000€&nbsp;HT</strong>
							</div>
						</div>
					</div>
					<div className='column-third'>
						<div className='offer-card offer-card--premium'>
							<h3>Refonte Premium</h3>
							<ul className='offer-features no-prefix'>
								<li>Analyse UX parcours clients</li>
								<li>Direction artistique sur-mesure</li>
								<li>Développement sur-mesure</li>
								<li>Architecture évolutive</li>
								<li>Documentation & formation</li>
							</ul>
							<div className='offer-price'>
								<strong>à partir de 6000€&nbsp;HT</strong>
							</div>
						</div>
					</div>
					<div className='column-third'>
						<div className='offer-card'>
							<h3>Intégration Front</h3>
							<ul className='offer-features no-prefix'>
								<li>Intégration de vos maquettes (Figma, Adobe XD, etc.)</li>
								<li>Respect des chartes graphiques</li>
								<li>Structure modulaire et évolutive</li>
								<li>Animations et interactions fluides</li>
								<li>Site rapide et fiable</li>
							</ul>
							<div className='offer-price'>
								<strong>TJM 450–500€</strong>
							</div>
						</div>
					</div>
				</div>
			</section>

			<div className='column-full align-center'>
				<Button
					to={`${process.env.PUBLIC_URL}/nous-contacter`}
					version='1'
				>
					Nous contacter
				</Button>
			</div>

			<section className='homepage-realisations'>
				<h2>Votre agence web en Hauts-De-France</h2>
				<p className='light'>
					Concrètement&nbsp;? On conçoit des sites, des applications et des expériences interactives pour vos dispositifs tactiles.<br />
					<br />
					Besoin d’un site institutionnel&nbsp;? D’une plateforme e-commerce&nbsp;? De site dédié à la prospection&nbsp;?
					Que vous soyez une TPE, PME, PMI ou même une association, on crée la solution adaptée à vos besoins&nbsp;!<br />
					<br />
					Nos deux piliers&nbsp;? Une expérience fluide et des designs responsive.
				</p>

				<ul className='columns homepage-realisations-list no-prefix'>
					{ loading && <Loader /> }
					{ (projects.length > 0) && 
						projects.map((project, i) => (
							<li
								className='column-third align-center mobile-margin-bottom'
								key={i}
							>
								<Card
									id={project.id}
									type="realisation"
									thumbnail={`/realisations/${project.id}/thumbnail.jpg`}
									title={project.title}
									description={project.description}
									tags={project.tags}
								/>
							</li>
						))
					}
				</ul>

				<div className='columns'>
					<div className='column-full align-center'>
						<Button to={`${process.env.PUBLIC_URL}/realisations-sur-mesure`}>Parcourir nos réalisations</Button>
					</div>
				</div>
			</section>

			<section className='homepage-team align-center'>
				<h2>Nous sommes BIG et Nouf</h2>
				<div className='team-photo-wrapper'>
					<img 
						src={require('../images/marion_et_francois.jpg')} 
						alt="Marion et François - BIG et Nouf"
						className='team-photo'
					/>
				</div>
				<p className='light'>
					Marion (design, stratégie) et François (intégration, développement).<br />
					Un binôme créatif et technique au service de vos projets web, avec attention et efficacité.
				</p>
			</section>
		</motion.main>
	)
}

export default Homepage
